import React, { FC, useContext, useEffect, useState } from "react";
import { useIntl, defineMessages } from "react-intl";
import { useErrorController } from "@natera/platform/lib/hooks";
import {
  Progress,
  UpdateCommunicationForm,
  LinkHeap,
  Notification,
  SetupProfileReviewInfo,
} from "@app/components";
import { ProfileContext } from "@app/provider/profile";
import { ResultCodes } from "@app/service/resultCodes";
import { SetupProfileForm } from "@app/components/forms";
import { FactorContainerSignUp } from "@app/components/factorList";
import {
  IntlContext,
  MfaSetupContext,
  NotificationContext,
  OrderRenewalContext,
  UppAuthContext,
  UserContext,
} from "@app/provider";
import { STAGE } from "@app/service/user";
import SpinnerView from "@app/components/spinnerView";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { routes } from "@app/routing";
import acknowledgements from "@etc/acknowledgements.json";
import { Communication, SetupProfileData } from "@app/provider/profile/types";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import {
  ORGANIC_SIGN_UP_TOTAL_STEPS,
  getOrganicSetupProfileDisplayStep,
  getSignUpFlowProgressValue,
} from "@app/utils/signUpFlowSteps";
import "./setupProfile.scss";

const messages = defineMessages({
  setupProfileStep: {
    id: "setupProfileStep",
    defaultMessage: "Step {number} of {count}",
  },
  setupProfileTitle: {
    id: "setupProfileTitle",
    defaultMessage:
      "You’ve successfully activated your account! Let’s set up your profile.",
  },
  setupProfileTitleReviewInfo: {
    id: "setupProfileTitleReviewInfo",
    defaultMessage: "Review your information",
  },
  setupProfilePatientInformationBar: {
    id: "setupProfilePatientInformationBar",
    defaultMessage:
      "Information entered needs to match what is on file with your clinic, which normally matches what is on your insurance card. If information entered here does not match exactly with the information provided by your clinic, your test information will not be visible on your account.",
  },
  setupProfileTwoFactorTitle: {
    id: "setupProfileTwoFactorTitle",
    defaultMessage: "Two-Factor Authentication",
  },
  setupProfileUserAlreadyHaveLinkedProfileError: {
    id: "setupProfileUserAlreadyHaveLinkedProfileError",
    defaultMessage: "You already have linked profile",
  },
  setupProfileAlreadyLinked: {
    id: "setupProfileAlreadyLinked",
    defaultMessage:
      "Our records show that you already have an account with us. Please log in with the existing account.",
  },
  setupProfileLogin: {
    id: "setupProfileLogin",
    defaultMessage: "Log In",
  },
  setupProfileMobileValidationError: {
    id: "setupProfileMobileValidationError",
    defaultMessage: "You must enter a US based number to create an account.",
  },
  setupProfilesWeAreSorry: {
    id: "setupProfilesWeAreSorry",
    defaultMessage:
      "We're sorry. An error occurred when setting up your profile.",
  },
  setupProfilesContactSupport: {
    id: "setupProfilesContactSupport",
    defaultMessage: "Contact support",
  },
  setupProfileCommunicationPreferencesTitle: {
    id: "setupProfileCommunicationPreferencesTitle",
    defaultMessage: "Communication Preferences",
  },
});

const SetupProfile: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useContext(IntlContext);

  const {
    isLoading: isProfileLoading,
    uppUser,
    loadProfile,
    updateUser,
  } = useContext(UserContext);
  const { profile: uppProfile } = React.useContext(UppAuthContext);
  const { startRenewalWorkflow } = React.useContext(OrderRenewalContext);

  const { addNotification, clear } = useContext(NotificationContext);
  const [step, setStep] = useState(0);
  const [filledProfileData, setFilledProfileData] =
    useState<SetupProfileData>();
  const [isReviewInfoScreen, setIsReviewInfoScreen] = useState(false);

  const errorController = useErrorController();

  const {
    setupProfileIsLoading: isLoading,
    setupProfile,
    setupProfileError,
    setupProfileData,
    profileData,
    skipCommunicationPreferencesStep,
    updatePatientCommunication,
    updatePatientCommunicationIsLoading,
  } = useContext(ProfileContext);
  const { enrolledFactor } = React.useContext(MfaSetupContext);

  useEffect(() => {
    loadProfile();
  }, []);

  // not sure if makes sense as an extra prevention (if user manually goes to /setupProfile route), maybe should remove or place elsewhere? (same with confirmation.tsx)
  useEffect(() => {
    if (uppUser?.invite) {
      navigate(routes.confirmation + location.search, { replace: true });
    }
  }, [uppUser?.invite, navigate, location.search]);

  useEffect(() => {
    if (uppProfile?.patientUID) {
      startRenewalWorkflow();
    }
  }, [uppProfile]);

  useEffect(() => {
    if (setupProfileError === ResultCodes.PROFILE_ALREADY_LINKED_ERROR) {
      addNotification({
        type: "error",
        message: intl.formatMessage(messages.setupProfileAlreadyLinked),
        actions: (
          <Link to={routes.logOut}>
            {intl.formatMessage(messages.setupProfileLogin)}
          </Link>
        ),
      });
    } else if (
      setupProfileError === ResultCodes.USER_ALREADY_HAVE_LINKED_PROFILE
    ) {
      addNotification({
        type: "error",
        message: intl.formatMessage(
          messages.setupProfileUserAlreadyHaveLinkedProfileError
        ),
      });
    } else if (setupProfileError === ResultCodes.PHONE_VALIDATION_FAILED) {
      errorController.setValidationError(
        "mobileNumber",
        intl.formatMessage(messages.setupProfileMobileValidationError)
      );
    } else if (
      setupProfileError === ResultCodes.PROFILE_SETUP_ERROR ||
      setupProfileError
    ) {
      addNotification({
        type: "error",
        message: intl.formatMessage(messages.setupProfilesWeAreSorry),
        actions: (
          <LinkHeap
            target="_blank"
            key="privacyPolicy"
            rel="noreferrer"
            to={acknowledgements.links.contactUs}
            heapEventName={HEAP_EVENTS.upp_click_contactnatera}
            heapEventProps={{
              location: HeapEventLocation.update_info_error,
            }}
          >
            {intl.formatMessage(messages.setupProfilesContactSupport)}
          </LinkHeap>
        ),
      });
    }
  }, [setupProfileError]);

  useEffect(() => {
    if (
      uppUser?.stage === STAGE.COMMUNICATION_PREFERENCES &&
      skipCommunicationPreferencesStep
    ) {
      updateUser({ stage: STAGE.FILLED });
    }
  }, [skipCommunicationPreferencesStep, uppUser?.stage]);

  useEffect(() => {
    if (uppUser) {
      switch (uppUser.stage) {
        case STAGE.BLANK:
          if (!uppUser.invite) {
            updateUser({ stage: STAGE.PROFILE_SETUP });
          }
          setStep(1);
          break;
        case STAGE.DATE_OF_BIRTH_VERIFICATION:
        case STAGE.CONFIRMATION: {
          if (uppUser.invite) {
            navigate(routes.confirmation + location.search, {
              replace: true,
            });
          } else {
            updateUser({ stage: STAGE.PROFILE_SETUP });
            setStep(1);
          }
          break;
        }
        case STAGE.PROFILE_SETUP:
          setStep(1);
          break;
        case STAGE.MFA_SETUP:
          setStep(2);
          break;
        case STAGE.COMMUNICATION_PREFERENCES:
          setStep(3);
          break;
        default:
          navigate(routes.home + location.search, { replace: true });
      }
    }
  }, [uppUser]);

  const handleProfileFormSubmit = async (profileData: SetupProfileData) => {
    setFilledProfileData(profileData);
    setIsReviewInfoScreen(true);
  };

  const handleReviewInfoEdit = () => {
    setIsReviewInfoScreen(false);
    heap.track(HEAP_EVENTS.upp_patientinfo_edit);
  };

  const handleReviewInfoSubmit = async () => {
    clear();
    await setupProfile({
      ...filledProfileData,
      email: uppUser?.email,
      language: {
        code: currentLanguage,
      },
    });
    loadProfile();
  };

  const handleCommunicationFormSubmit = async (
    communicationData: Communication[]
  ) => {
    await updatePatientCommunication(communicationData, {
      heapEventLocation: HeapEventLocation.accountCreation,
    });
    loadProfile();
  };

  const getTitle = () => {
    switch (step) {
      case 1: {
        return isReviewInfoScreen ? (
          <div className="review-info-title">
            {intl.formatMessage(messages.setupProfileTitleReviewInfo)}
          </div>
        ) : (
          <div className="set-up-profile-title">
            {intl.formatMessage(messages.setupProfileTitle)}
          </div>
        );
      }
      case 2: {
        return (
          <div className="two-factor-title">
            {intl.formatMessage(messages.setupProfileTwoFactorTitle)}
          </div>
        );
      }
    }
  };

  const factorListProfileData = setupProfileData || profileData;
  const displayStep =
    step !== 0 ? getOrganicSetupProfileDisplayStep(step) : undefined;

  return (
    <article className="setup-profile__container">
      <SpinnerView isLoading={isProfileLoading} />
      {step !== 3 && displayStep && (
        <Progress
          className="setup-profile__progress"
          value={getSignUpFlowProgressValue(
            displayStep,
            ORGANIC_SIGN_UP_TOTAL_STEPS
          )}
        />
      )}
      <div className="setup-profile__wrapper">
        <section className="setup-profile__content">
          {step !== 3 && step !== 0 && displayStep && (
            <h2 className="step-number">
              {intl.formatMessage(messages.setupProfileStep, {
                number: displayStep,
                count: ORGANIC_SIGN_UP_TOTAL_STEPS,
              })}
            </h2>
          )}
          {!enrolledFactor && (
            <h3 className="setup-profile__title">{getTitle()}</h3>
          )}
          {step === 1 && (
            <Notification
              key={"patientInformationBarId"}
              type="warning"
              className={
                isReviewInfoScreen
                  ? undefined
                  : "patient-information-bar_fade-in"
              }
            >
              {intl.formatMessage(messages.setupProfilePatientInformationBar)}
            </Notification>
          )}
          {step === 1 &&
            (isReviewInfoScreen && filledProfileData ? (
              <SetupProfileReviewInfo
                profileData={filledProfileData}
                onEdit={handleReviewInfoEdit}
                onSubmit={handleReviewInfoSubmit}
                isLoading={isLoading}
              />
            ) : (
              <SetupProfileForm
                initialProfileData={filledProfileData}
                onSubmit={handleProfileFormSubmit}
                clearErrors={errorController.clearErrors}
                clearValidationError={errorController.clearValidationError}
                setValidationError={errorController.setValidationError}
                getValidationError={errorController.getValidationError}
              />
            ))}
          {step === 2 && uppUser?.askForMfa && (
            <FactorContainerSignUp
              isTokenizedFlow={false}
              profile={{ ...factorListProfileData, email: uppUser.email }}
            />
          )}
          {step === 3 && !skipCommunicationPreferencesStep && (
            <UpdateCommunicationForm
              communicationData={profileData?.communicationPreferences}
              handleSubmit={handleCommunicationFormSubmit}
              className="communication-preferences"
              isLoading={updatePatientCommunicationIsLoading}
              isProfileSetup
            />
          )}
        </section>
      </div>
    </article>
  );
};

export default SetupProfile;
